import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import {
  transformBookingToFrontend,
  transformBookingToBackend,
} from "@/services/transformers";
import { getServiceName, getDoctorName } from "@/utils/dataLookups";

// Helper function to enrich bookings with related data
const enrichBookingsWithRelatedData = async (bookings, queryClient) => {
  // Get related services data to enrich bookings
  const servicesData = await queryClient.ensureQueryData({
    queryKey: ["services"],
    queryFn: () => apiClient.get("/api/services"),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Get unique practice IDs from bookings to fetch doctors for all relevant practices
  const practiceIds = [
    ...new Set(bookings.map((booking) => booking.practice_id).filter(Boolean)),
  ];

  // If no bookings or no practice IDs, use empty doctors array
  let doctorsData = [];
  if (practiceIds.length > 0) {
    // Fetch doctors for all practices involved in the bookings
    const doctorPromises = practiceIds.map((practiceId) =>
      queryClient.ensureQueryData({
        queryKey: ["doctors", practiceId],
        queryFn: () => apiClient.get("/api/doctors", { practiceId }),
        staleTime: 10 * 60 * 1000, // 10 minutes
      })
    );

    const doctorArrays = await Promise.all(doctorPromises);
    doctorsData = doctorArrays.flat(); // Combine all doctors from all practices
  }

  // Fetch patient profiles for all unique patient database IDs
  const patientIds = [
    ...new Set(bookings.map((booking) => booking.patient_database_id).filter(Boolean)),
  ];

  let patientsData = [];
  if (patientIds.length > 0 && practiceIds.length > 0) {
    try {
      // Fetch patients from the first practice (since patients are practice-isolated)
      const practiceId = practiceIds[0];
      patientsData = await queryClient.ensureQueryData({
        queryKey: ["patients", practiceId],
        queryFn: () => apiClient.get("/api/patients"),
        staleTime: 10 * 60 * 1000, // 10 minutes
      });
    } catch (error) {
      console.error("Failed to fetch patient data:", error);
      patientsData = [];
    }
  }

  // Create a map for quick patient lookup
  const patientMap = new Map(
    patientsData.map((patient) => [patient.id, patient])
  );

  // Enrich bookings with related data
  return bookings.map((booking) => {
    const patient = patientMap.get(booking.patient_database_id);

    return {
      ...booking,
      // Add service name lookup
      service_name: getServiceName(booking.service_id, servicesData),
      // Add doctor name lookup
      doctor_name: getDoctorName(booking.doctor_id, doctorsData),
      // Keep provider_name for backward compatibility (null when no doctor assigned)
      provider_name: booking.doctor_id
        ? getDoctorName(booking.doctor_id, doctorsData) || booking.doctor_id
        : null,
      // Add patient name from patient profile
      patient_name: patient?.name || null,
    };
  });
};

// Query Keys
export const bookingKeys = {
  all: ["bookings"],
  slots: (serviceId, date, doctorId) => [
    "bookings",
    "slots",
    { serviceId, date, doctorId },
  ],
  availableDates: (serviceId, doctorId, days) => [
    "bookings",
    "available-dates",
    { serviceId, doctorId, days },
  ],
  availableDoctors: (serviceId, date, startTime, endTime) => [
    "bookings",
    "available-doctors",
    { serviceId, date, startTime, endTime },
  ],
  bookings: (filters) => ["bookings", "list", filters],
  byDate: (date) => ["bookings", "by-date", date],
  byPatient: (patientId, date) => ["bookings", "patient", patientId, date],
  doctorAvailability: (doctorId) => [
    "bookings",
    "doctor-availability",
    doctorId,
  ],
  roomAvailability: (roomId) => ["bookings", "room-availability", roomId],
};

// Available Dates Hook - Returns array of dates and comprehensive availability data
export const useAvailableDates = (serviceId, doctorId = null, days = 60) => {
  return useQuery({
    queryKey: bookingKeys.availableDates(serviceId, doctorId, days),
    queryFn: async () => {
      const params = { serviceId, days };
      if (doctorId) params.doctorId = doctorId;
      const response = await apiClient.get("/api/bookings/available-dates", params);
      
      // Handle both old format (array) and new format (object with dates/availability)
      if (Array.isArray(response)) {
        // Old format - just dates
        return { dates: response, availability: [] };
      } else {
        // New format - comprehensive data
        return response;
      }
    },
    enabled: !!serviceId,
    staleTime: 5 * 60 * 1000, // 5 minutes - dates don't change as frequently
  });
};

// Available Slots Hook - Gets slots from cached availability data or API fallback
export const useAvailableSlots = (serviceId, date) => {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: bookingKeys.slots(serviceId, date, null), // Always use null for doctorId in key
    queryFn: async () => {
      // First try to get slots from cached availability data
      const availabilityQueryKey = bookingKeys.availableDates(serviceId, null, 60);
      const cachedAvailability = queryClient.getQueryData(availabilityQueryKey);
      
      if (cachedAvailability?.availability) {
        const dateEntry = cachedAvailability.availability.find(item => item.date === date);
        if (dateEntry?.slots) {
          return dateEntry.slots;
        }
      }
      
      // Fallback to API call if not found in cache
      const params = { serviceId, date };
      return await apiClient.get("/api/bookings/slots", params);
    },
    enabled: !!serviceId && !!date,
    staleTime: 2 * 60 * 1000, // 2 minutes - slots change frequently
  });
};

// Available Doctors Hook
export const useAvailableDoctors = (serviceId, date, startTime, endTime) => {
  return useQuery({
    queryKey: bookingKeys.availableDoctors(serviceId, date, startTime, endTime),
    queryFn: async () => {
      return await apiClient.get("/api/bookings/available-doctors", {
        serviceId,
        date,
        startTime,
        endTime,
      });
    },
    enabled: !!serviceId && !!date && !!startTime && !!endTime,
    staleTime: 1 * 60 * 1000, // 1 minute - doctor availability changes quickly
  });
};

// All Bookings Hook - Enriched with related data
export const useBookings = (filters = {}) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: bookingKeys.bookings(filters),
    queryFn: async () => {
      // Get bookings data
      const response = await apiClient.get("/api/bookings", filters);
      const bookings = response.map(transformBookingToFrontend);

      // Enrich with related data
      return await enrichBookingsWithRelatedData(bookings, queryClient);
    },
    staleTime: 30 * 1000, // 30 seconds
  });
};

// Bookings by Date Hook - Enriched with related data
export const useBookingsByDate = (date) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: bookingKeys.byDate(date),
    queryFn: async () => {
      // Get bookings data
      const response = await apiClient.get(`/api/bookings/by-date/${date}`);
      const bookings = response.map(transformBookingToFrontend);

      // Enrich with related data
      return await enrichBookingsWithRelatedData(bookings, queryClient);
    },
    enabled: !!date,
    staleTime: 30 * 1000, // 30 seconds
  });
};

// Patient Bookings Hook - Enriched with related data
export const usePatientBookings = (patientId, date = null) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: bookingKeys.byPatient(patientId, date),
    queryFn: async () => {
      // Get bookings data
      const params = date ? { date } : {};
      const response = await apiClient.get(
        `/api/bookings/patient/${patientId}`,
        params
      );
      const bookings = response.map(transformBookingToFrontend);

      // Enrich with related data
      return await enrichBookingsWithRelatedData(bookings, queryClient);
    },
    enabled: !!patientId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

// Doctor Availability Hook
export const useDoctorAvailability = (doctorId = null) => {
  return useQuery({
    queryKey: bookingKeys.doctorAvailability(doctorId),
    queryFn: async () => {
      const params = doctorId ? { doctorId } : {};
      return await apiClient.get("/api/bookings/doctor-availability", params);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - availability doesn't change often
  });
};

// Room Availability Hook
export const useRoomAvailability = (roomId = null) => {
  return useQuery({
    queryKey: bookingKeys.roomAvailability(roomId),
    queryFn: async () => {
      const params = roomId ? { roomId } : {};
      return await apiClient.get("/api/bookings/room-availability", params);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Create Booking Mutation
export const useCreateBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bookingData) => {
      const transformedData = transformBookingToBackend(bookingData);
      const response = await apiClient.post("/api/bookings", transformedData);
      return transformBookingToFrontend(response);
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch relevant queries
      queryClient.invalidateQueries({ queryKey: bookingKeys.all });

      // Invalidate slots for the specific service/date
      queryClient.invalidateQueries({
        queryKey: bookingKeys.slots(variables.service_id, variables.date),
      });

      // Invalidate bookings for the specific date
      queryClient.invalidateQueries({
        queryKey: bookingKeys.byDate(variables.date),
      });

      // Invalidate patient bookings
      if (variables.patient_id) {
        queryClient.invalidateQueries({
          queryKey: bookingKeys.byPatient(variables.patient_id),
        });
      }
    },
  });
};

// Update Booking Status Mutation
export const useUpdateBookingStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ bookingId, status }) => {
      const response = await apiClient.patch(
        `/api/bookings/${bookingId}/status`,
        { status }
      );
      return transformBookingToFrontend(response);
    },
    onSuccess: () => {
      // Invalidate all booking-related queries
      queryClient.invalidateQueries({ queryKey: bookingKeys.all });
    },
  });
};

// Update Full Booking Mutation
export const useUpdateBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bookingData) => {
      const { id, ...updateData } = bookingData;
      const transformedData = transformBookingToBackend(updateData);
      const response = await apiClient.patch(
        `/api/bookings/${id}`,
        transformedData
      );
      return transformBookingToFrontend(response);
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch relevant queries
      queryClient.invalidateQueries({ queryKey: bookingKeys.all });

      // Invalidate slots for the specific service/date
      queryClient.invalidateQueries({
        queryKey: bookingKeys.slots(variables.service_id, variables.date),
      });

      // Invalidate bookings for the specific date
      queryClient.invalidateQueries({
        queryKey: bookingKeys.byDate(variables.date),
      });

      // Invalidate patient bookings
      if (variables.patient_id) {
        queryClient.invalidateQueries({
          queryKey: bookingKeys.byPatient(variables.patient_id),
        });
      }
    },
  });
};

// Cancel Booking Mutation
export const useCancelBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bookingId) => {
      return await apiClient.delete(`/api/bookings/${bookingId}`);
    },
    onSuccess: () => {
      // Invalidate all booking-related queries
      queryClient.invalidateQueries({ queryKey: bookingKeys.all });
    },
  });
};
